/**
 * De officiële relatietabellen van ArchiMate 4 (C260 Appendix B.5).
 *
 * **Waarom dit bestand bestaat.** Onze `archimate-matrix.json` is een
 * handgebouwde benadering: laag- en aspect-clausules plus een paar honderd
 * expliciete paren. Die kent **afgeleide** relaties niet, en die zijn in
 * ArchiMate gewoon toegestaan — is A toegewezen aan B en realiseert B een C,
 * dan mag je A→C tekenen. Gemeten op een ArchiSurance-bestand keurde onze
 * matrix acht relaties af die de taal toestaat, allemaal van dat soort. Dat
 * was ook de reden dat de AMEFF-import alleen meldt en niet omzet.
 *
 * De tabellen hieronder zijn overgenomen uit de zes afbeeldingen van Appendix
 * B.5. Ze staan in de spec (ook in de HTML-editie) alleen als plaatje, dus dit
 * is overtypwerk — en dat betekent dat er fouten in kunnen zitten. Daarom:
 *
 *  - Elke cel hoort minstens een association te bevatten (`o`/`O`); een cel
 *    zonder is een leesfout. Bewaakt door een test.
 *
 * **Alle 1764 cellen zijn op 2026-08-29 nagelopen** tegen uitsnedes van de
 * afbeeldingen, tabel voor tabel, met Gerald aan de andere kant. Dat leverde
 * 31 correcties op — genoeg om te weten dat overtypen zonder die ronde niet
 * betrouwbaar is. De vaakst gemaakte fout: een `i` (assignment) of `r`
 * (realization) middenin een lange lettercode over het hoofd zien; negen keer
 * ging het mis in de kolom Grouping van de Common-tabel. Blijft er een cel
 * verdacht, leg 'm dan naast het plaatje: de blokken hieronder staan in
 * dezelfde vorm als de tabellen, één regel per rij.
 *  - Alles wat onze eigen matrix vandaag toestaat, moet in deze tabellen ook
 *    toegestaan zijn — de taal is immers ruimer dan onze benadering. Elke
 *    afwijking is óf een overtypfout, óf een plek waar onze matrix te ruim
 *    was. Bewaakt door een test die de afwijkingen bij naam noemt.
 *
 * **Leesrichting.** In de tabellen is de kolom de bron (`From →`) en de rij het
 * doel (`To ↓`). De blokken hieronder staan per kolom-groep, met één regel per
 * rij-type in de vaste volgorde van `RIJEN`.
 *
 * **Lettercodes.** Hoofdletter = een directe relatie uit het metamodel,
 * kleine letter = een afgeleide. Voor de vraag "mag dit" maakt dat niet uit —
 * allebei mogen getekend worden — dus we lezen hoofdletter-ongevoelig.
 *
 * `g` staat voor aggregatie **én** compositie: C260 §5.1.2 stelt dat een
 * compositie is toegestaan in precies dezelfde gevallen als een aggregatie, en
 * de tabellen delen daarom één letter. Er is geen aparte `c`.
 */

/** Rij-volgorde (`To ↓`), gelijk in alle zes de tabellen. */
const RIJEN = [
  "Collaboration",
  "Event",
  "Function",
  "Grouping",
  "Location",
  "Path",
  "Process",
  "Role",
  "Service",
  "Assessment",
  "Driver",
  "Goal",
  "Meaning",
  "Outcome",
  "Principle",
  "Requirement",
  "Stakeholder",
  "Value",
  "Capability",
  "CourseOfAction",
  "Resource",
  "ValueStream",
  "BusinessActor",
  "BusinessInterface",
  "BusinessObject",
  "Product",
  "ApplicationComponent",
  "ApplicationInterface",
  "DataObject",
  "Artifact",
  "CommunicationNetwork",
  "Device",
  "DistributionNetwork",
  "Equipment",
  "Facility",
  "Material",
  "Node",
  "SystemSoftware",
  "TechnologyInterface",
  "Deliverable",
  "Plateau",
  "WorkPackage",
] as const;

/** Letter → relatietype. `g` dekt aggregatie en compositie (zie boven). */
const LETTER_NAAR_RELATIES: Record<string, readonly string[]> = {
  a: ["archimate.Access"],
  f: ["archimate.Flow"],
  g: ["archimate.Aggregation", "archimate.Composition"],
  i: ["archimate.Assignment"],
  n: ["archimate.Influence"],
  o: ["archimate.Association"],
  r: ["archimate.Realization"],
  s: ["archimate.Specialization"],
  t: ["archimate.Triggering"],
  v: ["archimate.Serving"],
};

/** Eén blok per kolom-groep: de bron-types, en per rij hun codes. */
type Blok = { kolommen: readonly string[]; regels: string };

const MOTIVATION: Blok = {
  kolommen: [
    "Assessment",
    "Driver",
    "Goal",
    "Meaning",
    "Outcome",
    "Principle",
    "Requirement",
    "Stakeholder",
    "Value",
  ],
  regels: `
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    Ogns Ogns Ogns Ogns Ognrs Ognrs Ognrs Ogns Ogns
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    GNOS NO NO NO NO NO NO NO NO
    NO GNOS NO NO NO NO NO NO NO
    NO NO GNOS NO NOR NOr NOr NO NO
    NO NO NO GNOS NO NO NO NO NO
    NO NO NO NO GNOS NOR NOR NO NO
    NO NO NO NO NO GNOS NOR NO NO
    NO NO NO NO NO NO GNOS NO NO
    NO NO NO NO NO NO NO GNOS NO
    NO NO NO NO NO NO NO NO GNOS
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
    O O O O O O O O O
  `,
};

const STRATEGY: Blok = {
  kolommen: ["Capability", "CourseOfAction", "Resource", "ValueStream"],
  regels: `
    O O O O
    O O O O
    O O O O
    Ofgnrstv Ofgnrstv Ofginrstv Ofgnrstv
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    On On On On
    On On On On
    Onr Onr Onr Onr
    On On On On
    Onr NOR Onr Onr
    Onr Onr Onr Onr
    NOR NOR NOR NOR
    On On On On
    On On On On
    FGOSTV FOTV IOftv FOTV
    FORTV FGOSTV IOfrtv FORTV
    Oftv Oftv GOSftv Oftv
    FOTV FOTV IOftv FGOSTV
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
    O O O O
  `,
};

const COMMON: Blok = {
  kolommen: [
    "Collaboration",
    "Event",
    "Function",
    "Grouping",
    "Location",
    "Path",
    "Process",
    "Role",
    "Service",
  ],
  regels: `
    GOSftv Oftv Oftv GOfrstv GOftv Oftv Oftv Oftv OVft
    Ofirtv GOSftv FOTv GOfirstv GOfirtv Oftv FOTv IOftv FOTv
    Ofirtv FOTv FGOSTv GOfirstv GOfirtv Oftv FGOTv IOftv OVft
    Oafginrstv Oafgnrstv Oafgnrstv GOSafinrtv GOafinrstv Oafgnrstv Oafgnrstv Oafginrstv Oafgnrstv
    Oftv Oftv Oftv GOfrstv GOSftv Oftv Oftv Oftv Oftv
    ORftv Oftv Oftv GOfrstv GOfrtv GORSftv Oftv ORftv OVft
    Ofirtv FOTv FGOTv GOfirstv GOfirtv Oftv FGOSTv IOftv OVft
    GIOfrtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv GIOSftv OVft
    Ofirtv FOTv ORftv GOfirstv GOfirtv Oftv ORftv Ofirtv FGOSTv
    On On On GOns GOn On On On On
    On On On GOns GOn On On On On
    Onr Onr Onr GOnrs GOnr Onr Onr Onr Onr
    On On On GOns GOn On On On On
    Onr Onr Onr GOnrs GOnr Onr Onr Onr Onr
    Onr Onr Onr GOnrs GOnr Onr Onr Onr Onr
    NOR NOR NOR GNORs GNOR NOR NOR NOR NOR
    Oin On On GOinrs GOin On On On On
    On On On GOns GOn On On On On
    Or O OR GOfirstv GOr Or OR Or OR
    Or O OR GOfirstv GOr Or OR Or OR
    OR O O GOfrstv GOR OR O OR O
    Or O OR GOfirstv GOr Or OR Or OR
    GOfitv Oftv Oftv GOfirstv GOfitv Oftv Oftv Oftv OVft
    Ofgirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv GOfirtv OVft
    Oai AO AO GOairs GOai Oa AO Oa AO
    Oftv Oftv Oftv GOfrstv GOftv Oftv Oftv Oftv Oftv
    GOfrtv Oftv Oftv GOfrstv GOfrtv Oftv Oftv Oftv OVft
    Ofgirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv GOfirtv OVft
    Oai AO AO GOairs GOai Oa AO Oa AO
    Oai AO AO GOairs GOai Oa AO Oa AO
    Oftv Oftv Oftv GOfrstv GOftv Oftv Oftv Oftv OVft
    GOfirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv Oftv OVft
    Oftv Oftv Oftv GOfrstv GOftv Oftv Oftv Oftv OVft
    GOfirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv Oftv OVft
    GOfitv Oftv Oftv GOfirstv GOfitv Oftv Oftv Oftv OVft
    Oai AO AO GOairs GOai Oa AO Oa AO
    GOfitv Oftv Oftv GOfirstv GOfitv Oftv Oftv Oftv OVft
    GOfirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv Oftv OVft
    Ofgirtv Oftv Oftv GOfirstv GOfirtv Oftv Oftv GOfitv OVft
    O AO O GOars GO O O O O
    O OT O GOfrst GO O O O O
    Oi FOT O GOfist GOi O O IO O
  `,
};

const BUSINESS_APPLICATION: Blok = {
  kolommen: [
    "BusinessActor",
    "BusinessInterface",
    "BusinessObject",
    "Product",
    "ApplicationComponent",
    "ApplicationInterface",
    "DataObject",
  ],
  regels: `
    Oftv Oftv O Oftv Oftv Oftv O
    Ofitv Oftv O Ofrtv Ofitv Oftv O
    Ofitv Oftv O Ofrtv Ofitv Oftv O
    Oafginrstv Oafginrstv Ognrs Oafgnrstv Oafginrstv Oafginrstv Ognrs
    Oftv Oftv O Oftv Oftv Oftv O
    ORftv ORftv O Ofrtv ORftv ORftv O
    Ofitv Oftv O Ofrtv Ofitv Oftv O
    IOftv Oftv O Ofrtv IOftv Oftv O
    Ofirtv IOftv O GOfrtv Ofirtv IOftv O
    On On On On On On On
    On On On On On On On
    Onr Onr Onr Onr Onr Onr Onr
    On On On On On On On
    Onr Onr Onr Onr Onr Onr Onr
    Onr Onr Onr Onr Onr Onr Onr
    NOR NOR NOR NOR NOR NOR NOR
    IOn On On On On On On
    On On On On On On On
    Or Or Or Or Or Or Or
    Or Or Or Or Or Or Or
    OR OR OR OR OR OR OR
    Or Or Or Or Or Or Or
    GOSftv Oftv O Oftv Oftv Oftv O
    GOfirtv GOSftv O Ofrtv Ofirtv ORftv O
    IOa Oa GOS GOa Oa Oa OR
    Oftv Oftv O GOSftv Oftv Oftv O
    Oftv Oftv O Ofrtv GOSftv Oftv O
    Ofirtv Oftv O Ofrtv GOfirtv GOSftv O
    Oa Oa O GOa IOa Oa GOS
    Oa Oa O GOa Oa Oa O
    Oftv Oftv O Oftv Oftv Oftv O
    Oftv Oftv O Ofrtv Oftv Oftv O
    Oftv Oftv O Oftv Oftv Oftv O
    Oftv Oftv O Ofrtv Oftv Oftv O
    Oftv Oftv O Oftv Oftv Oftv O
    Oa Oa O GOa Oa Oa O
    Oftv Oftv O Oftv Oftv Oftv O
    Oftv Oftv O Ofrtv Oftv Oftv O
    Ofitv Oftv O Ofrtv Ofitv Oftv O
    O O OS O O O OS
    O O O O O O O
    Oi O O O Oi O O
  `,
};

const TECHNOLOGY: Blok = {
  kolommen: [
    "Artifact",
    "CommunicationNetwork",
    "Device",
    "DistributionNetwork",
    "Equipment",
    "Facility",
    "Material",
    "Node",
    "SystemSoftware",
    "TechnologyInterface",
  ],
  regels: `
    O Oftv Oftv Oftv Oftv Oftv O Oftv Oftv Oftv
    Or Ofirtv Ofirtv Ofirtv Ofirtv Ofirtv Or Ofirtv Ofirtv Oftv
    Or Ofirtv Ofirtv Ofirtv Ofirtv Ofirtv Or Ofirtv Ofirtv Oftv
    Ognrs Oafginrstv Oafginrstv Oafginrstv Oafginrstv Oafginrstv Ognrs Oafginrstv Oafginrstv Oafginrstv
    O Oftv Oftv Oftv Oftv Oftv O Oftv Oftv Oftv
    Or ORftv ORftv ORftv ORftv ORftv Or ORftv ORftv ORftv
    Or Ofirtv Ofirtv Ofirtv Ofirtv Ofirtv Or Ofirtv Ofirtv Oftv
    Or Ofirtv IOfrtv Ofirtv IOfrtv IOfrtv Or IOfrtv IOfrtv Oftv
    Or Ofirtv Ofirtv Ofirtv Ofirtv Ofirtv Or Ofirtv Ofirtv IOfrtv
    On On On On On On On On On On
    On On On On On On On On On On
    Onr Onr Onr Onr Onr Onr Onr Onr Onr Onr
    On On On On On On On On On On
    Onr Onr Onr Onr Onr Onr Onr Onr Onr Onr
    Onr Onr Onr Onr Onr Onr Onr Onr Onr Onr
    NOR NOR NOR NOR NOR NOR NOR NOR NOR NOR
    On On On Oin On Oin On Oin On On
    On On On On On On On On On On
    Or Or Or Or Or Or Or Or Or Or
    Or Or Or Or Or Or Or Or Or Or
    OR OR OR OR OR OR OR OR OR OR
    Or Or Or Or Or Or Or Or Or Or
    O Oftv Oftv GOfitv Oftv IOftv O Ofitv Oftv Oftv
    Or Ofirtv Ofirtv Ofgirtv Ofirtv Ofirtv Or Ofirtv Ofirtv Ofrtv
    OSr Oa Oa Oai Oa Oai ORS Oai Oa Oa
    O Oftv Oftv Oftv Oftv Oftv O Oftv Oftv Oftv
    OR Ofrtv Ofrtv Ofrtv Ofrtv Ofrtv Or Ofrtv Ofrtv Oftv
    Or Ofirtv Ofirtv Ofirtv Ofirtv Ofirtv Or Ofirtv Ofirtv ORftv
    ORS Oa Oa Oa Oa Oa OSr Oa Oa Oa
    GOSr Oai IOa Oai Oai Oai OSr Oai IOa Oa
    O GOSftv Oftv Oftv Oftv Oftv O Oftv Oftv Oftv
    OR GOfrtv GOSfrtv Ofgirtv GOfrtv Ofgirtv OR GOfirtv Ofrtv Oftv
    O Oftv Oftv GOSftv Oftv Oftv O Oftv Oftv Oftv
    O Oftv Oftv GOfirtv GOSfrtv GOfirtv OR GOfirtv Oftv Oftv
    O Oftv Oftv GOfitv Oftv GOSfitv O GOfitv Oftv Oftv
    OS Oa Oa Oai IOa Oai GOSr Oai Oa Oa
    O Oftv Oftv Ofgitv Oftv GIOftv O GOSfitv Oftv Oftv
    OR GOfirtv IOfrtv Ofgirtv Ofirtv Ofgirtv Or GOfirtv GIOSfrtv Oftv
    Or Ofgirtv GOfirtv Ofgirtv GOfirtv GOfirtv Or GOfirtv GOfirtv GOSftv
    OS O O O O O OS O O O
    O O O O O O O O O O
    O Oi Oi Oi Oi Oi O Oi Oi O
  `,
};

const IMPLEMENTATION: Blok = {
  kolommen: ["Deliverable", "Plateau", "WorkPackage"],
  regels: `
    OR GOR OR
    OR GORT FORT
    OR GOR OR
    Ognrs Oafginrst Oafgnrst
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    On On On
    On On On
    Onr GOnr Onr
    On On On
    Onr GOnr Onr
    Onr Onr Onr
    NOR GNOR NOR
    Onr Oinr Onr
    On On On
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    OR GOR OR
    GOS Oa AOR
    OR GOSTf Ofrt
    O Oft FGOST
  `,
};

const BLOKKEN = [MOTIVATION, STRATEGY, COMMON, BUSINESS_APPLICATION, TECHNOLOGY, IMPLEMENTATION];

/** `from|to` → set van toegestane relatietype-ids. */
function bouwIndex(): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();
  for (const blok of BLOKKEN) {
    const regels = blok.regels
      .split("\n")
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    if (regels.length !== RIJEN.length) {
      throw new Error(
        `relatietabel: ${regels.length} regels, verwacht ${RIJEN.length} (kolommen: ${blok.kolommen.join(",")})`,
      );
    }
    regels.forEach((regel, i) => {
      const cellen = regel.split(/\s+/);
      if (cellen.length !== blok.kolommen.length) {
        throw new Error(
          `relatietabel: rij ${RIJEN[i]} heeft ${cellen.length} cellen, verwacht ${blok.kolommen.length}`,
        );
      }
      cellen.forEach((cel, k) => {
        const from = `archimate.${blok.kolommen[k]}`;
        const to = `archimate.${RIJEN[i]}`;
        const set = index.get(`${from}|${to}`) ?? new Set<string>();
        for (const letter of cel.toLowerCase()) {
          for (const rel of LETTER_NAAR_RELATIES[letter] ?? []) set.add(rel);
        }
        index.set(`${from}|${to}`, set);
      });
    });
  }
  return index;
}

let index: Map<string, Set<string>> | null = null;

/** Staat de officiële tabel deze relatie toe? `false` ook wanneer we het paar
 *  niet kennen — de tabellen dekken alleen de 42 ArchiMate-types. */
export function officialTableAllows(
  relationTypeId: string,
  sourceTypeId: string,
  targetTypeId: string,
): boolean {
  if (!index) index = bouwIndex();
  return index.get(`${sourceTypeId}|${targetTypeId}`)?.has(relationTypeId) ?? false;
}

/** Alle paren die de tabellen kennen — voor tests en controles. */
export function officialTablePairs(): ReadonlyMap<string, ReadonlySet<string>> {
  if (!index) index = bouwIndex();
  return index;
}
